<template>
  <div class="m-2">
    <div class="my-2">
      <div
          v-if="currentEmpTotalWeight === 100"
          class="bg-green-100 text-green-700 font-semibold px-4 py-2 rounded-lg mb-3"
      >
        Goals submitted correctly: 100% weight.
      </div>
      <div
          v-else-if="currentEmpTotalWeight < 100"
          class="bg-yellow-100 text-yellow-700 font-semibold px-4 py-2 rounded-lg mb-3"
      >
        Warning: Total weight below 100% ({{ currentEmpTotalWeight }}%).
      </div>
      <div
          v-else
          class="bg-red-100 text-red-700 font-semibold px-4 py-2 rounded-lg mb-3"
      >
        Error: Total weight exceeds 100% ({{ currentEmpTotalWeight }}%).
      </div>
      <ve-table
        v-if="tableData.length"
        :columns="columns"
        :table-data="tableData"
      />

      <div
        v-else-if="!loading && tableData.length == 0"
        class="flex flex-col justify-center items-center h-full mt-5"
      >
        <el-empty :image-size="70" description="No Data"></el-empty>
      </div>
      <transition name="fade">
        <div
          v-if="loading"
          class="inset-0 absolute z-50 bg-black bg-opacity-10 w-full"
        >
          <loader class="center-h-v" />
        </div>
      </transition>
    </div>
    <EditGoal
      :info="info"
      :showEditGoal="showEditGoal"
      :handleEditCallback="handleEditCallback"
      @close="close"
      :refresh="refresh"
    ></EditGoal>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import { mapGetters } from "vuex";
import Loader from "@/components/loader/loader.vue";

export default {
  name: "ObjectivesGoals",
  components: {
    EditGoal: () =>
      import("@/components/PerformanceReview/objectives/editGoal.vue"),
    Loader,
  },
  data() {
    return {
      showEditGoal: false,
      info: null,
      loading: false,
      activePeriod: null,
      columns: [
        { field: "GoalName", key: "a", title: "Goal Name", align: "left" },
        {
          field: "GoalMeasure",
          key: "b",
          title: "Goal Measure",
          align: "left",
          renderBodyCell: ({ row }) => (
            <p class="capitalize">{row.GoalMeasure}</p>
          ),
        },
        {
          field: "Weight",
          key: "weight",
          title: "Weight",
          align: "center",
          renderBodyCell: ({ row }) => (row.Weight ? row.Weight : "...."),
        },
        {
          field: "StartDate",
          key: "c",
          title: "Start Date",
          align: "left",
          renderBodyCell: ({ row }) =>
            moment(row.StartDate).format("DD/MM/YYYY"),
        },
        {
          field: "EndDate",
          key: "x",
          title: "End Date",
          align: "left",
          renderBodyCell: ({ row }) => moment(row.EndDate).format("DD/MM/YYYY"),
        },
        {
          field: "Target",
          key: "d",
          title: "Target",
          align: "left",
        },
        {
          field: "Status",
          key: "k",
          title: "Status",
          align: "left",
          renderBodyCell: ({ row }) => (
              <p class="capitalize">{row.SubmissionStatus}</p>
          ),
        },
        {
          field: "Description",
          key: "f",
          title: "Description",
          align: "left",
        },
        {
          title: "Action",
          field: "",
          key: "action",
          renderBodyCell: ({ row }) => {
            if (row.SubmissionStatus !== "DRAFT") {
              return null;
            }

            return (
                <ve-popper
                    trigger="clickToOpen"
                    options={{
                      placement: "top",
                      modifiers: { offset: { offset: "0,10px" } },
                    }}
                >
                  <div class="bg-white p-2 shadow-md popper">
                    <button
                        class="w-full text-left py-1 px-2 hover:bg-primary hover:text-white"
                        style="border-radius: 0 !important"
                        onClick={() => this.handleEditButtonClick(row)}
                    >
                      Edit
                    </button>
                    <button
                        class="w-full text-left py-1 px-2 hover:bg-primary hover:text-white"
                        style="border-radius: 0 !important"
                        onClick={() => this.handleDeleteButtonClick(row)}
                    >
                      Delete
                    </button>
                  </div>

                  <button slot="reference">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        id="Outline"
                        viewBox="0 0 24 24"
                        width="20"
                        height="20"
                    >
                      <circle cx="12" cy="2" r="2" />
                      <circle cx="12" cy="12" r="2" />
                      <circle cx="12" cy="22" r="2" />
                    </svg>
                  </button>
                </ve-popper>
            );
          },
        },

      ],
      tableData: [],
    };
  },
  created() {
    this.getPerformancePeriodSetups()
  },
  computed: {
    ...mapGetters(["getOrganization", "getToken"]),
    currentEmpTotalWeight() {
      return this.tableData.reduce((sum, obj) => sum + obj.Weight, 0);
    },
  },
  methods: {
    refresh() {
     this.getPerformancePeriodSetups()
    },
    close() {
      this.showEditGoal = false;
    },
    getPerformancePeriodSetups() {
      this.loading = true;
      let url = `${this.getOrganization}/performance/period/${this.$ApiVersion}/request`;

      const options = {
        headers: {
          Authorization: `Bearer ${this.getToken}`,
        },
      };
      axios.get(url, options).then(
          (response) => {
            this.loading = false;
            if (response.data.StatusCode == 200) {
              this.activePeriod=response.data.Data.find(
                  (period) => period.Active == true
              );
                this.$store.commit('setReviewPeriod', this.activePeriod.Name)
              this.fetchData();
            } else {
              this.$notify.error({
                title: 'Failure',
                message: response.StatusMessage,
              });
            }
          },
          (error) => {
            this.loading = false;
            this.$notify.error({ title: 'Failure', message: error.message });
          }
      );
    },
    fetchData() {
      this.loading = true;
      const options = {
        headers: {
          Authorization: `Bearer ${this.getToken}`,
        },
        params:{
          periodId:this.activePeriod.Id
        }
      };
      const apiUrl = `${this.getOrganization}/performance/objective/mine/${this.$ApiVersion}/request`;

      axios
        .get(apiUrl, options)
        .then((response) => {
          this.loading = false;
          if (response.data.StatusCode == 200) {
            this.tableData = response.data.Data;
          } else {
            this.$notify.error({
              title: "Failure",
              message: "Error occured while fetching data.",
            });
          }
        })
        .catch((error) => {
          this.loading = false;
          this.$notify.error({
            title: "Failure",
            message: "Error occured while fetching records.",
          });
          console.error("Error fetching data:", error);
        });
    },

    handleEditCallback() {
      this.showEditGoal = false;
      this.fetchData();
    },

    handleAddButtonClick() {
      this.fetchData();
    },

    handleEditButtonClick(row) {
      this.info = {
        ...row,
        ObjectiveId: row.ObjectiveId,
        GoalName: row.GoalName,
        StartDate: row.StartDate
          ? new Date(row.StartDate).toISOString().split("T")[0]
          : "",
        EndDate: row.EndDate
          ? new Date(row.EndDate).toISOString().split("T")[0]
          : "",
        GoalMeasure: row.GoalMeasure,
      };

      this.showEditGoal = true;
    },

    handleDeleteButtonClick({ ObjectiveId }) {
      const options = {
        headers: {
          Authorization: `Bearer ${this.getToken}`,
        },
        data: {
          ObjectiveId,
        },
      };
      const apiUrl = `${this.getOrganization}/performance/objective/v1/delete`;

      axios
        .delete(apiUrl, options)
        .then((response) => {
          if (response.status == 200) {
            this.$notify({
              title: "Success",
              message: response.data.StatusMessage,
              type: "success",
            });
            this.fetchData();
          } else {
            this.$notify.error({
              title: "Failure",
              message: response.data.StatusMessage,
            });
          }
        })
        .catch((error) => {
          this.$notify.error({
            title: "Failure",
            message: "Error occured while deleting the record.",
          });
          console.error("Error fetching data:", error);
        });
    },
  },
};
</script>
